// WebView Bridge 유틸리티 함수들
import { saveFcmToken } from './fcmToken'

// 브릿지 메시지 타입
export interface BridgeMessage {
  type: string
  data: any
  id?: string
}

// FCM 토큰 응답 타입
export interface FcmTokenResponse {
  token: string
  platform: 'ios' | 'android'
}

// 디바이스 정보 타입
export interface DeviceInfo {
  platform: 'ios' | 'android'
  model: string
  version: string
  appVersion: string
}

// WebView 환경 확인
export const isWebView = (): boolean => {
  try {
    // ReactNativeBridge가 존재하고 sendMessage 함수가 있는지 확인
    if (window.ReactNativeBridge && typeof window.ReactNativeBridge.sendMessage === 'function') {
      return true
    }
    // 구버전 WebView 지원
    if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
      return true
    }
    return false
  } catch (error) {
    console.error(error)
    // 웹 환경에서 에러 발생 시 false 반환
    return false
  }
}

// 브릿지 메시지 전송
export const sendBridgeMessage = (type: string, data: any = {}): void => {
  console.log(`[WebView Bridge] 메시지 전송 시도 - Type: ${type}`, data)

  if (!isWebView()) {
    console.warn('Not running in WebView environment')
    return
  }

  // 새로운 브릿지 API 우선 사용
  if (window.ReactNativeBridge) {
    try {
      console.log(`[WebView Bridge] ReactNativeBridge.sendMessage 호출 - Type: ${type}`)
      window.ReactNativeBridge.sendMessage(type, data)
      console.log(`[WebView Bridge] ReactNativeBridge.sendMessage 성공 - Type: ${type}`)
      return
    } catch (error) {
      console.error('Bridge error:', error)
    }
  }

  // 구 버전 브릿지 API 폴백
  if (window.ReactNativeWebView) {
    try {
      const message = JSON.stringify({ type, payload: data })
      console.log(`[WebView Bridge] ReactNativeWebView.postMessage 호출 - Type: ${type}`)
      window.ReactNativeWebView.postMessage(message)
      console.log(`[WebView Bridge] ReactNativeWebView.postMessage 성공 - Type: ${type}`)
    } catch (error) {
      console.error('WebView postMessage error:', error)
    }
  }
}

// 브릿지 메시지 리스너 등록
export const registerBridgeListener = (callback: (message: BridgeMessage) => void): (() => void) => {
  if (!window.ReactNativeBridge) {
    console.warn('ReactNativeBridge not available')
    return () => {}
  }

  window.ReactNativeBridge.onMessage(callback)

  // 리스너 해제 함수 반환
  return () => {
    // 현재 브릿지 API에는 리스너 해제 기능이 없으므로 빈 함수 반환
    console.log('Bridge listener cleanup')
  }
}

// FCM 토큰 요청
export const requestFcmToken = (): Promise<FcmTokenResponse | null> => {
  console.log('[FCM] FCM 토큰 요청 시작')

  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('FCM token request failed: Not in WebView')
      resolve(null)
      return
    }

    console.log('[FCM] 웹뷰 환경 확인됨, 타임아웃 설정 중...')

    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      console.warn('[FCM] FCM token request timeout - 5초 경과')
      resolve(null)
    }, 5000)

    console.log('[FCM] 브릿지 리스너 등록 중...')

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      console.log('[FCM] 브릿지 메시지 수신:', message.type, message.data)

      if (message.type === 'fcmToken') {
        console.log('[FCM] fcmToken 메시지 수신!')
        clearTimeout(timeout)
        cleanup()

        // FCM 토큰 저장
        if (message.data && message.data.token) {
          saveFcmToken(message.data.token)
          console.log('[FCM] FCM token saved:', message.data.token.substring(0, 20) + '...')
        } else {
          console.warn('[FCM] FCM 토큰이 응답에 없습니다:', message.data)
        }

        resolve(message.data as FcmTokenResponse)
      }
    })

    console.log('[FCM] getFcmToken 메시지 전송 중...')

    // FCM 토큰 요청
    sendBridgeMessage('getFcmToken')

    console.log('[FCM] getFcmToken 메시지 전송 완료')
  })
}

// 디바이스 정보 요청
export const requestDeviceInfo = (): Promise<DeviceInfo | null> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('Device info request failed: Not in WebView')
      resolve(null)
      return
    }

    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      console.warn('Device info request timeout')
      resolve(null)
    }, 5000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'deviceInfo') {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data as DeviceInfo)
      }
    })

    // 디바이스 정보 요청
    sendBridgeMessage('getDeviceInfo')
  })
}

// 알림 권한 상태 확인
export const checkNotificationPermission = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      // 웹뷰가 아닌 경우 브라우저 Notification API 사용
      if ('Notification' in window) {
        resolve(Notification.permission === 'granted')
      } else {
        resolve(false)
      }
      return
    }

    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      console.warn('Notification permission check timeout')
      resolve(false)
    }, 5000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'notificationPermission') {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.status === 'granted')
      }
    })

    // 알림 권한 확인 요청
    sendBridgeMessage('getNotificationPermission')
  })
}

// 알림 권한 요청
export const requestNotificationPermission = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      // 웹뷰가 아닌 경우 브라우저 Notification API 사용
      if ('Notification' in window) {
        Notification.requestPermission().then(permission => {
          resolve(permission === 'granted')
        })
      } else {
        resolve(false)
      }
      return
    }

    // 타임아웃 설정 (10초 - 사용자 응답 대기)
    const timeout = setTimeout(() => {
      console.warn('Notification permission request timeout')
      resolve(false)
    }, 10000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'notificationPermissionResult') {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.granted === true)
      }
    })

    // 알림 권한 요청
    sendBridgeMessage('requestNotificationPermission')
  })
}

// 토픽 구독
export const subscribeToTopic = (topic: string): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('Topic subscription failed: Not in WebView')
      resolve(false)
      return
    }

    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      console.warn('Topic subscription timeout')
      resolve(false)
    }, 5000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'topicSubscriptionResult' && message.data.topic === topic) {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.success === true)
      }
    })

    // 토픽 구독 요청
    sendBridgeMessage('subscribeToTopic', { topic })
  })
}

// 토픽 구독 해제
export const unsubscribeFromTopic = (topic: string): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('Topic unsubscription failed: Not in WebView')
      resolve(false)
      return
    }

    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      console.warn('Topic unsubscription timeout')
      resolve(false)
    }, 5000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'topicSubscriptionResult' && message.data.topic === topic) {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.success === true)
      }
    })

    // 토픽 구독 해제 요청
    sendBridgeMessage('unsubscribeFromTopic', { topic })
  })
}

// 앱 비밀번호 설정 상태 확인
export const checkAppPasscode = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('App passcode check failed: Not in WebView')
      resolve(false)
      return
    }

    // 타임아웃 설정 (5초)
    const timeout = setTimeout(() => {
      console.warn('App passcode check timeout')
      resolve(false)
    }, 5000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'checkPasscodeResult') {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.isSet === true)
      }
    })

    // 앱 비밀번호 설정 상태 확인 요청
    sendBridgeMessage('checkPasscode', { requestId: Date.now().toString() })
  })
}

// 앱 비밀번호 설정 요청
export const setAppPasscode = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('App passcode set failed: Not in WebView')
      resolve(false)
      return
    }

    // 타임아웃 설정 (60초 - 사용자 입력 및 앱 전환 시간 고려)
    const timeout = setTimeout(() => {
      console.warn('App passcode set timeout')
      resolve(false)
    }, 60000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'setPasscodeResult') {
        clearTimeout(timeout)
        cleanup()
        if (message.data.cancelled) {
          console.log('App passcode set cancelled by user')
          resolve(false)
        } else {
          resolve(message.data.success === true)
        }
      }
    })

    // 앱 비밀번호 설정 요청
    sendBridgeMessage('setPasscode', { requestId: Date.now().toString() })
    console.log('Sent setPasscode request to native app')
  })
}

// 앱 비밀번호 확인 요청
export const verifyAppPasscode = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('App passcode verify failed: Not in WebView')
      resolve(false)
      return
    }

    // 타임아웃 설정 (30초 - 사용자 입력 대기)
    const timeout = setTimeout(() => {
      console.warn('App passcode verify timeout')
      resolve(false)
    }, 30000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'passcodeResult') {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.success === true)
      }
    })

    // 앱 비밀번호 확인 요청
    sendBridgeMessage('requestPasscode', { requestId: Date.now().toString() })
  })
}

// 앱 비밀번호 초기화 요청
export const resetAppPasscode = (): Promise<boolean> => {
  return new Promise(resolve => {
    if (!isWebView()) {
      console.warn('App passcode reset failed: Not in WebView')
      resolve(false)
      return
    }

    // 타임아웃 설정 (10초)
    const timeout = setTimeout(() => {
      console.warn('App passcode reset timeout')
      resolve(false)
    }, 10000)

    // 응답 리스너 등록
    const cleanup = registerBridgeListener(message => {
      if (message.type === 'resetPasscodeResult') {
        clearTimeout(timeout)
        cleanup()
        resolve(message.data.success === true)
      }
    })

    // 앱 비밀번호 초기화 요청
    sendBridgeMessage('resetPasscode', { requestId: Date.now().toString() })
  })
}

// 로그인 상태를 네이티브 앱에 알림
export const notifyAuthStatus = (isLoggedIn: boolean): void => {
  if (!isWebView()) {
    return
  }

  // 네이티브 앱에 로그인 상태 전달
  sendBridgeMessage('authStatusChanged', {
    isLoggedIn,
    timestamp: Date.now()
  })
}

// 네이티브 채팅 페이지 열기
export const openNativeChatPage = (consultationId: number, returnUrl?: string): void => {
  console.log(`[WebView Bridge] 네이티브 채팅 페이지 열기 요청 - consultationId: ${consultationId}`)

  if (!isWebView()) {
    console.warn('Not running in WebView environment - 채팅 페이지 열기 불가')
    return
  }

  // 브릿지 메시지 전송
  sendBridgeMessage('CHAT:OPEN_CHAT_PAGE', {
    consultationId,
    returnUrl: returnUrl || window.location.pathname,
    timestamp: Date.now()
  })

  console.log('[WebView Bridge] CHAT:OPEN_CHAT_PAGE 메시지 전송 완료')
}
